import React from 'react';
import { X, HardDrive, AlertTriangle, CheckCircle } from 'lucide-react';
import { Notebook } from '../types';
import { getNotebookSizeBreakdown, formatBytes } from '../utils/sizeUtils';

interface NotebookSizeModalProps {
    notebook: Notebook;
    language: 'english' | 'vietnamese';
    onClose: () => void;
    onJumpToNode?: (nodeId: string) => void;
}

export const NotebookSizeModal: React.FC<NotebookSizeModalProps> = ({
    notebook,
    language,
    onClose,
    onJumpToNode
}) => {
    const breakdown = getNotebookSizeBreakdown(notebook);
    const isVietnamese = language === 'vietnamese';

    const getSizeColor = (percentage: number) => {
        if (percentage >= 90) return 'text-red-600 dark:text-red-400';
        if (percentage >= 80) return 'text-yellow-600 dark:text-yellow-400';
        if (percentage >= 70) return 'text-blue-600 dark:text-blue-400';
        return 'text-green-600 dark:text-green-400';
    };

    const getSizeIcon = (percentage: number) => {
        if (percentage >= 80) return <AlertTriangle className="text-yellow-500" size={20} />;
        return <CheckCircle className="text-green-500" size={20} />;
    };

    const percentage = parseFloat(breakdown.percentage);

    return (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
            <div className="bg-white dark:bg-slate-900 rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-hidden flex flex-col">
                {/* Header */}
                <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-slate-700">
                    <div className="flex items-center gap-3">
                        <HardDrive className="text-blue-500" size={24} />
                        <div>
                            <h2 className="text-xl font-bold text-gray-900 dark:text-white">
                                {isVietnamese ? 'Phân tích Dung lượng' : 'Size Analysis'}
                            </h2>
                            <p className="text-sm text-gray-500 dark:text-gray-400">
                                {notebook.title}
                            </p>
                        </div>
                    </div>
                    <button
                        onClick={onClose}
                        className="p-2 hover:bg-gray-100 dark:hover:bg-slate-800 rounded-lg transition-colors"
                    >
                        <X size={20} />
                    </button>
                </div>

                {/* Content */}
                <div className="flex-1 overflow-y-auto p-6 space-y-6">
                    {/* Overall Size */}
                    <div className="bg-gradient-to-br from-blue-50 to-indigo-50 dark:from-blue-950/20 dark:to-indigo-950/20 rounded-xl p-6">
                        <div className="flex items-center justify-between mb-4">
                            <div className="flex items-center gap-3">
                                {getSizeIcon(percentage)}
                                <div>
                                    <p className="text-sm text-gray-600 dark:text-gray-400">
                                        {isVietnamese ? 'Tổng dung lượng' : 'Total Size'}
                                    </p>
                                    <p className={`text-3xl font-bold ${getSizeColor(percentage)}`}>
                                        {breakdown.totalKB} KB
                                    </p>
                                </div>
                            </div>
                            <div className="text-right">
                                <p className="text-sm text-gray-600 dark:text-gray-400">
                                    {isVietnamese ? 'Đã dùng' : 'Used'}
                                </p>
                                <p className={`text-2xl font-bold ${getSizeColor(percentage)}`}>
                                    {breakdown.percentage}%
                                </p>
                            </div>
                        </div>

                        {/* Progress Bar */}
                        <div className="w-full bg-gray-200 dark:bg-slate-700 rounded-full h-3 overflow-hidden">
                            <div
                                className={`h-full transition-all duration-500 ${percentage >= 90 ? 'bg-red-500' :
                                        percentage >= 80 ? 'bg-yellow-500' :
                                            percentage >= 70 ? 'bg-blue-500' :
                                                'bg-green-500'
                                    }`}
                                style={{ width: `${Math.min(percentage, 100)}%` }}
                            />
                        </div>

                        <div className="flex justify-between mt-2 text-xs text-gray-500 dark:text-gray-400">
                            <span>0 KB</span>
                            <span>950 KB {isVietnamese ? '(Giới hạn)' : '(Limit)'}</span>
                        </div>
                    </div>

                    {/* Statistics */}
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                        <div className="bg-gray-50 dark:bg-slate-800 rounded-lg p-4">
                            <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">
                                {isVietnamese ? 'Số notes' : 'Notes'}
                            </p>
                            <p className="text-2xl font-bold text-gray-900 dark:text-white">
                                {breakdown.nodeCount}
                            </p>
                        </div>
                        <div className="bg-gray-50 dark:bg-slate-800 rounded-lg p-4">
                            <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">
                                {isVietnamese ? 'TB/note' : 'Avg/note'}
                            </p>
                            <p className="text-2xl font-bold text-gray-900 dark:text-white">
                                {breakdown.avgNodeSizeKB} KB
                            </p>
                        </div>
                        <div className="bg-gray-50 dark:bg-slate-800 rounded-lg p-4">
                            <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">
                                {isVietnamese ? 'Có ảnh' : 'w/ Images'}
                            </p>
                            <p className="text-2xl font-bold text-gray-900 dark:text-white">
                                {breakdown.breakdown.withImages}
                            </p>
                        </div>
                        <div className="bg-gray-50 dark:bg-slate-800 rounded-lg p-4">
                            <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">
                                {isVietnamese ? 'Có AI' : 'w/ AI'}
                            </p>
                            <p className="text-2xl font-bold text-gray-900 dark:text-white">
                                {breakdown.breakdown.withAnalysis}
                            </p>
                        </div>
                    </div>

                    {/* Heaviest Nodes */}
                    <div>
                        <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3">
                            {isVietnamese ? '🔥 Top 10 notes nặng nhất' : '🔥 Top 10 Heaviest Notes'}
                        </h3>
                        <div className="space-y-2">
                            {breakdown.heaviestNodes.map((node, index) => {
                                const sizeKB = (node.size / 1000).toFixed(1);
                                const nodePercentage = ((node.size / breakdown.total) * 100).toFixed(1);

                                return (
                                    <div
                                        key={node.id}
                                        onClick={() => onJumpToNode?.(node.id)}
                                        className={`flex items-center justify-between p-3 rounded-lg border border-gray-200 dark:border-slate-700 ${onJumpToNode ? 'cursor-pointer hover:bg-gray-50 dark:hover:bg-slate-800 transition-colors' : ''
                                            }`}
                                    >
                                        <div className="flex items-center gap-3 flex-1 min-w-0">
                                            <span className={`text-xs font-bold w-6 h-6 flex items-center justify-center rounded-full ${index === 0 ? 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400' :
                                                    index === 1 ? 'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-400' :
                                                        index === 2 ? 'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400' :
                                                            'bg-gray-50 text-gray-600 dark:bg-slate-800 dark:text-gray-500'
                                                }`}>
                                                {index + 1}
                                            </span>
                                            <div className="flex-1 min-w-0">
                                                <p className="font-medium text-gray-900 dark:text-white truncate">
                                                    {node.title || (isVietnamese ? 'Chưa đặt tên' : 'Untitled')}
                                                </p>
                                                <div className="flex items-center gap-2 text-xs text-gray-500 dark:text-gray-400">
                                                    {node.hasImage && <span>📷</span>}
                                                    {node.hasDrawing && <span>✏️</span>}
                                                    {node.attachmentCount > 0 && <span>📎 {node.attachmentCount}</span>}
                                                    {node.hasAnalysis && <span>🤖</span>}
                                                    {node.reviewCount > 0 && <span>📝 {node.reviewCount}</span>}
                                                </div>
                                            </div>
                                        </div>
                                        <div className="text-right">
                                            <p className="font-bold text-gray-900 dark:text-white">
                                                {sizeKB} KB
                                            </p>
                                            <p className="text-xs text-gray-500 dark:text-gray-400">
                                                {nodePercentage}%
                                            </p>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </div>

                    {/* Tips */}
                    {percentage > 70 && (
                        <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg p-4">
                            <h3 className="font-semibold text-yellow-800 dark:text-yellow-400 mb-2">
                                💡 {isVietnamese ? 'Gợi ý tối ưu' : 'Optimization Tips'}
                            </h3>
                            <ul className="text-sm text-yellow-700 dark:text-yellow-300 space-y-1">
                                <li>• {isVietnamese ? 'Xóa notes không cần thiết' : 'Delete unnecessary notes'}</li>
                                <li>• {isVietnamese ? 'Nén hoặc xóa ảnh lớn' : 'Compress or remove large images'}</li>
                                <li>• {isVietnamese ? 'Chia thành nhiều notebooks nhỏ hơn' : 'Split into smaller notebooks'}</li>
                                {breakdown.breakdown.withReviews > 20 && (
                                    <li>• {isVietnamese ? 'Review history sẽ tự động giới hạn còn 10 entries' : 'Review history will auto-limit to 10 entries'}</li>
                                )}
                            </ul>
                        </div>
                    )}
                </div>

                {/* Footer */}
                <div className="border-t border-gray-200 dark:border-slate-700 p-4 bg-gray-50 dark:bg-slate-800/50">
                    <button
                        onClick={onClose}
                        className="w-full py-2 bg-blue-500 hover:bg-blue-600 text-white font-medium rounded-lg transition-colors"
                    >
                        {isVietnamese ? 'Đóng' : 'Close'}
                    </button>
                </div>
            </div>
        </div>
    );
};
